import { useEffect, useRef, useState } from "react";
import { useChatStore } from "../../../stores/ChatStore";
import useAccessToken from "../../../hooks/AccessToken";
import { useIntl } from "react-intl";
import useHandleCloseModal from "../../../hooks/HandleCloseModal";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { switchSessionChat } from "../../../api/ChatCRUD";
import useSWR from "swr";
import axios from "axios";
import { SelectOption } from "../../../interfaces/SelectOption";
import VirtualizedSelect from "../../Select/SelectVirtualized";
import useUser from "../../../hooks/User";
import * as Yup from 'yup';

interface Props {
}

const SwitchSessionChat = ({ }: Props) => {
    const API_URL = process.env.REACT_APP_API_URL
    const token = useAccessToken()
    const intl = useIntl()
    const user = useUser()
    const { selectedChatPhone, setShowChat, setIsOpenDetailContact, setPhoneNumber } = useChatStore()
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const [listUsers, setListUsers] = useState<SelectOption[]>([]);
    const [modalBtnLoading, setModalBtnLoading] = useState(false)

    const fetcher = async (url: string) => {
        const response = await axios.get(url, {
            headers: {
                'X-Auth-token': token,
            },
        });
        return response.data.data;
    };

    const getUrl = () => {
        if (user.data.is_superadmin) return `${API_URL}chat-agent/admin/phones`
        else return `${API_URL}chat-agent/agent/phones`
    }

    const { data: dataUsers = [], isLoading: isLoadingDataUsers } = useSWR(getUrl(), fetcher)

    const formik = useFormik({
        initialValues: {
            users: '',
        },
        validationSchema: Yup.object().shape({
            users: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.USER" }) })),
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setModalBtnLoading(true)

            const { listChatAgents, phoneNumber } = useChatStore.getState();

            try {
                const res = await switchSessionChat(listChatAgents[phoneNumber][listChatAgents[phoneNumber].length - 1].id, values.users, token)
                if (res.data.success) {
                    closeModalRef.current?.click();
                    setShowChat(false)
                    setIsOpenDetailContact(false)
                    setPhoneNumber('')
                    Swal.fire({
                        icon: "success",
                        title: intl.formatMessage({ id: "ALERT.TITLE.SUCCESS" }),
                        text: `${intl.formatMessage({ id: "FORM.VALIDATION.SWITCH_SUCCESS" })}.`,
                        heightAuto: false,
                    })
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    heightAuto: false,
                    text: error.response.data.message,
                })
            } finally {
                setModalBtnLoading(false)
            }
        }
    });

    useEffect(() => {
        if (!isLoadingDataUsers) {
            const userFound = dataUsers.find((user: any) => user.id === selectedChatPhone[0].id);
            const data = userFound?.agents?.filter((val: any) => val.id !== user.data.id).map((val: any) => ({
                label: val.name,
                value: val.id
            })) || [];

            setListUsers(data);
        }
    }, [dataUsers])

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    return (
        <div className="modal fade" tabIndex={-1} id="switch-session-chat-modal" ref={modalRef}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <form onSubmit={formik.handleSubmit} noValidate>
                        <div className="modal-body">
                            <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.USER' })}</label>
                            <VirtualizedSelect
                                options={listUsers}
                                isLoading={isLoadingDataUsers}
                                isDisabled={isLoadingDataUsers}
                                className="w-100"
                                placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: 'FORM.LABEL.USER' })}`}
                                value={listUsers.find(option => option.value === formik.values.users) || null}
                                onChange={(option) =>
                                    formik.handleChange({
                                        target: {
                                            name: "users",
                                            value: option?.value || ""
                                        }
                                    })
                                }
                                onBlur={
                                    () => formik.values.users === "" && formik.setFieldTouched("users", true)
                                }
                            />
                            {formik.touched.users && formik.errors.users && (
                                <div className='text-danger fv-plugins-message-container'>
                                    <span role='alert' className="text-danger">{formik.errors.users}</span>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-light d-none"
                                data-bs-dismiss="modal"
                                ref={closeModalRef}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                            </button>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleCloseModal}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                            </button>
                            <button type="submit" className={`btn btn-primary me-10 ${modalBtnLoading ? 'disabled' : ''}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                                <span className="indicator-label">
                                    {intl.formatMessage({ id: 'FORM.ACTION.SAVE' })}
                                </span>
                                <span className="indicator-progress">
                                    Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export { SwitchSessionChat }